import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ThemeClasses } from "../../../types/components";
import type { FormField, RepeatColumn } from "./ApiCrudSection";

// Renders the add/edit form from a section's field schema and returns the
// collected values on submit. Pure presentational + local form state.
interface EntityFormProps {
  fields: FormField[];
  initial?: Record<string, unknown>;
  themeClasses: ThemeClasses;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (values: Record<string, string>) => void;
}

export default function EntityForm({
  fields,
  initial,
  themeClasses,
  submitLabel,
  onCancel,
  onSubmit,
}: EntityFormProps) {
  const { t } = useTranslation("common");
  const [values, setValues] = useState<Record<string, string>>(() => {
    const base: Record<string, string> = {};
    for (const f of fields) {
      if (f.type === "repeat") {
        // Repeat fields carry an array of rows, stored serialized as JSON so
        // the form value map stays Record<string, string>.
        const seed = initial?.[f.key];
        base[f.key] = JSON.stringify(Array.isArray(seed) ? seed : []);
        continue;
      }
      const seed = initial?.[f.key] ?? f.defaultValue ?? "";
      base[f.key] = String(seed);
    }
    return base;
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const inputBase =
    "w-full rounded-lg border px-3 py-2 text-sm outline-none transition-colors bg-app border-line text-content focus:border-primary-500";

  const set = (key: string, v: string) =>
    setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = async () => {
    const nextErrors: Record<string, boolean> = {};
    for (const f of fields) {
      if (f.required && !values[f.key]?.trim()) nextErrors[f.key] = true;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields.map((f) => (
          <div
            key={f.key}
            className={
              f.type === "textarea" || f.type === "repeat"
                ? "sm:col-span-2"
                : ""
            }
          >
            <label
              className={`mb-1 block text-xs font-medium ${themeClasses.textSecondary}`}
            >
              {f.label}
              {f.required && <span className="ml-0.5 text-red-500">*</span>}
            </label>

            {f.type === "repeat" ? (
              <RepeaterField
                columns={f.repeatColumns ?? []}
                addRowLabel={f.addRowLabel ?? t("mantenimiento.addRow")}
                themeClasses={themeClasses}
                value={values[f.key]}
                onChange={(json) => set(f.key, json)}
              />
            ) : f.type === "select" ? (
              <select
                value={values[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              >
                <option value="">—</option>
                {f.options?.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : f.type === "textarea" ? (
              <textarea
                rows={3}
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              />
            ) : (
              <input
                type={
                  f.type === "number"
                    ? "number"
                    : f.type === "date"
                    ? "date"
                    : "text"
                }
                value={values[f.key]}
                placeholder={f.placeholder}
                onChange={(e) => set(f.key, e.target.value)}
                className={inputBase}
              />
            )}

            {errors[f.key] && (
              <p className="mt-1 text-xs text-red-500">
                {t("mantenimiento.requiredField")}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onCancel}
          className={`rounded-lg border px-4 py-2 text-sm ${themeClasses.border} ${themeClasses.buttonText} ${themeClasses.buttonHover}`}
        >
          {t("mantenimiento.cancel")}
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary transition-colors hover:bg-primary-400 disabled:opacity-60"
        >
          {submitting ? t("mantenimiento.loading") : submitLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Line-items editor for `repeat` fields (services / parts / attachments).
// Reads & writes the field value as a JSON-serialized array of row objects.
// ---------------------------------------------------------------------------
type RepeaterRow = Record<string, string | number>;

interface RepeaterFieldProps {
  columns: RepeatColumn[];
  addRowLabel: string;
  themeClasses: ThemeClasses;
  value: string;
  onChange: (json: string) => void;
}

function RepeaterField({
  columns,
  addRowLabel,
  themeClasses,
  value,
  onChange,
}: RepeaterFieldProps) {
  const { t } = useTranslation("common");
  let rows: RepeaterRow[] = [];
  try {
    const parsed = JSON.parse(value || "[]");
    if (Array.isArray(parsed)) rows = parsed as RepeaterRow[];
  } catch {
    rows = [];
  }

  const cellBase =
    "w-full rounded-md border px-2 py-1.5 text-sm outline-none bg-app border-line text-content focus:border-primary-500";

  const commit = (next: RepeaterRow[]) => onChange(JSON.stringify(next));

  const addRow = () => {
    const blank: RepeaterRow = {};
    columns.forEach((c) => (blank[c.key] = c.type === "number" ? 0 : ""));
    commit([...rows, blank]);
  };

  const updateCell = (i: number, key: string, raw: string, isNum: boolean) => {
    const next = rows.map((r, idx) =>
      idx === i ? { ...r, [key]: isNum ? Number(raw) || 0 : raw } : r
    );
    commit(next);
  };

  const removeRow = (i: number) =>
    commit(rows.filter((_, idx) => idx !== i));

  return (
    <div
      className={`rounded-lg border p-2 ${themeClasses.border} bg-surface`}
    >
      {rows.length === 0 ? (
        <p className={`px-1 py-2 text-xs ${themeClasses.textSecondary}`}>
          {t("mantenimiento.noRows")}
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="flex items-end gap-2">
              {columns.map((c) => (
                <div key={c.key} className="flex-1">
                  <span
                    className={`mb-0.5 block text-[10px] ${themeClasses.textSecondary}`}
                  >
                    {c.label}
                  </span>
                  <input
                    type={c.type === "number" ? "number" : "text"}
                    value={String(row[c.key] ?? "")}
                    onChange={(e) =>
                      updateCell(i, c.key, e.target.value, c.type === "number")
                    }
                    className={cellBase}
                  />
                </div>
              ))}
              <button
                type="button"
                onClick={() => removeRow(i)}
                title={t("mantenimiento.delete")}
                className="mb-0.5 rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
              >
                <i className="ri-delete-bin-line" />
              </button>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={addRow}
        className="mt-2 flex items-center gap-1 rounded-md border border-dashed border-primary-500/50 px-3 py-1.5 text-xs text-primary-500 hover:bg-primary-500/10"
      >
        <i className="ri-add-line" />
        {addRowLabel}
      </button>
    </div>
  );
}
